import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';

const AdminContext = createContext();

// Initial state
const initialState = {
  users: [],
  companies: [],
  systemStats: {
    totalUsers: 0,
    totalCompanies: 0,
    totalShipments: 0,
    totalRevenue: 0,
    activeIntegrations: 0,
    systemHealth: 'good'
  },
  systemSettings: {
    maintenance: {
      enabled: false,
      message: '',
      scheduledStart: null,
      scheduledEnd: null
    },
    features: {
      registrationEnabled: true,
      paymentsEnabled: true,
      notificationsEnabled: true,
      analyticsEnabled: true,
      ecommerceEnabled: true
    },
    limits: {
      maxShipmentsPerMonth: 1000,
      maxUsersPerCompany: 10,
      maxIntegrationsPerUser: 5
    },
    notifications: {
      emailEnabled: true,
      smsEnabled: true,
      whatsappEnabled: true
    }
  },
  auditLogs: [],
  isLoading: false,
  error: null
};

// Action types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_USERS: 'SET_USERS',
  SET_COMPANIES: 'SET_COMPANIES',
  SET_SYSTEM_STATS: 'SET_SYSTEM_STATS',
  UPDATE_SYSTEM_SETTINGS: 'UPDATE_SYSTEM_SETTINGS',
  SET_AUDIT_LOGS: 'SET_AUDIT_LOGS',
  ADD_AUDIT_LOG: 'ADD_AUDIT_LOG',
  UPDATE_USER: 'UPDATE_USER',
  DELETE_USER: 'DELETE_USER',
  UPDATE_COMPANY: 'UPDATE_COMPANY',
  DELETE_COMPANY: 'DELETE_COMPANY'
};

// Reducer
function adminReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };
    
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };
    
    case ACTIONS.SET_USERS:
      return { ...state, users: action.payload, isLoading: false };
    
    case ACTIONS.SET_COMPANIES:
      return { ...state, companies: action.payload, isLoading: false };
    
    case ACTIONS.SET_SYSTEM_STATS:
      return { ...state, systemStats: action.payload, isLoading: false };
    
    case ACTIONS.UPDATE_SYSTEM_SETTINGS:
      return {
        ...state,
        systemSettings: { ...state.systemSettings, ...action.payload },
        isLoading: false
      };
    
    case ACTIONS.SET_AUDIT_LOGS:
      return { ...state, auditLogs: action.payload, isLoading: false };
    
    case ACTIONS.ADD_AUDIT_LOG:
      return {
        ...state,
        auditLogs: [action.payload, ...state.auditLogs].slice(0, 100) // Keep only last 100
      };
    
    case ACTIONS.UPDATE_USER:
      return {
        ...state,
        users: state.users.map(user =>
          user.id === action.payload.id ? action.payload : user
        ),
        isLoading: false
      };
    
    case ACTIONS.DELETE_USER:
      return {
        ...state,
        users: state.users.filter(user => user.id !== action.payload),
        isLoading: false
      };
    
    case ACTIONS.UPDATE_COMPANY:
      return {
        ...state,
        companies: state.companies.map(company =>
          company.id === action.payload.id ? action.payload : company
        ),
        isLoading: false
      };
    
    case ACTIONS.DELETE_COMPANY:
      return {
        ...state,
        companies: state.companies.filter(company => company.id !== action.payload),
        isLoading: false
      };
    
    default:
      return state;
  }
}

// Context Provider
export const AdminProvider = ({ children }) => {
  const [state, dispatch] = useReducer(adminReducer, initialState);
  const { user } = useAuth();

  // Load initial admin data
  useEffect(() => {
    if (user && user.role === 'admin') {
      loadAdminData();
    }
  }, [user]);

  const loadAdminData = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    
    try {
      // Simulate API calls
      await Promise.all([
        loadUsers(),
        loadCompanies(),
        loadSystemStats(),
        loadAuditLogs()
      ]);
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockUsers = [
        {
          id: 'user_001',
          name: 'Juan Pérez',
          email: 'juan@example.com',
          company: 'Empresa A',
          role: 'user',
          status: 'active',
          createdAt: '2025-01-10T10:00:00Z',
          lastLogin: '2025-01-16T08:30:00Z',
          shipmentsCount: 45,
          totalSpent: 12450.50
        },
        {
          id: 'user_002',
          name: 'María García',
          email: 'maria@empresa-b.com',
          company: 'Empresa B',
          role: 'admin',
          status: 'active',
          createdAt: '2025-01-08T14:20:00Z',
          lastLogin: '2025-01-16T09:15:00Z',
          shipmentsCount: 78,
          totalSpent: 23150.75
        },
        {
          id: 'user_003',
          name: 'Carlos Rodríguez',
          email: 'carlos@startup.mx',
          company: 'Startup MX',
          role: 'user',
          status: 'suspended',
          createdAt: '2025-01-05T16:45:00Z',
          lastLogin: '2025-01-14T12:00:00Z',
          shipmentsCount: 12,
          totalSpent: 3250.00
        }
      ];
      
      dispatch({ type: ACTIONS.SET_USERS, payload: mockUsers });
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const mockCompanies = [
        {
          id: 'comp_001',
          name: 'Empresa A',
          rfc: 'EMP123456789',
          address: 'Av. Reforma 123, CDMX',
          contactEmail: 'contacto@empresa-a.com',
          contactPhone: '+52 55 1234 5678',
          usersCount: 5,
          shipmentsCount: 234,
          totalSpent: 56780.25,
          status: 'active',
          createdAt: '2025-01-05T10:00:00Z'
        },
        {
          id: 'comp_002',
          name: 'Empresa B',
          rfc: 'EMB987654321',
          address: 'Calle Juárez 456, Guadalajara',
          contactEmail: 'info@empresa-b.com',
          contactPhone: '+52 33 9876 5432',
          usersCount: 3,
          shipmentsCount: 156,
          totalSpent: 34250.75,
          status: 'active',
          createdAt: '2025-01-02T15:30:00Z'
        }
      ];
      
      dispatch({ type: ACTIONS.SET_COMPANIES, payload: mockCompanies });
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  }, []);

  const loadSystemStats = useCallback(async () => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 600));
      
      const mockStats = {
        totalUsers: 23,
        totalCompanies: 8,
        totalShipments: 1456,
        totalRevenue: 156780.50,
        activeIntegrations: 12,
        systemHealth: 'good',
        monthlyGrowth: {
          users: 15.2,
          shipments: 28.7,
          revenue: 22.3
        },
        topCarriers: [
          { name: 'FedEx', percentage: 35 },
          { name: 'DHL', percentage: 28 },
          { name: 'UPS', percentage: 22 },
          { name: 'Estafeta', percentage: 15 }
        ]
      };
      
      dispatch({ type: ACTIONS.SET_SYSTEM_STATS, payload: mockStats });
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  }, []);

  const loadAuditLogs = useCallback(async () => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 700));
      
      const mockLogs = [
        {
          id: 'log_001',
          action: 'user_login',
          userId: 'user_001',
          userName: 'Juan Pérez',
          details: 'Usuario inició sesión exitosamente',
          ipAddress: '192.168.1.100',
          timestamp: '2025-01-16T09:30:00Z',
          severity: 'info'
        },
        {
          id: 'log_002',
          action: 'shipment_created',
          userId: 'user_002',
          userName: 'María García',
          details: 'Nuevo envío creado: SILVA1705401234',
          ipAddress: '192.168.1.105',
          timestamp: '2025-01-16T09:15:00Z',
          severity: 'info'
        },
        {
          id: 'log_003',
          action: 'failed_login',
          userId: null,
          userName: 'unknown',
          details: 'Intento de login fallido para: hacker@evil.com',
          ipAddress: '203.0.113.45',
          timestamp: '2025-01-16T08:45:00Z',
          severity: 'warning'
        },
        {
          id: 'log_004',
          action: 'admin_settings_changed',
          userId: 'admin_001',
          userName: 'Admin Silva',
          details: 'Configuración de sistema actualizada',
          ipAddress: '192.168.1.1',
          timestamp: '2025-01-16T08:00:00Z',
          severity: 'high'
        }
      ];
      
      dispatch({ type: ACTIONS.SET_AUDIT_LOGS, payload: mockLogs });
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
    }
  }, []);

  // User management
  const updateUser = useCallback(async (userId, updates) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const updatedUser = {
        ...state.users.find(u => u.id === userId),
        ...updates,
        updatedAt: new Date().toISOString()
      };
      
      dispatch({ type: ACTIONS.UPDATE_USER, payload: updatedUser });
      
      // Add audit log
      const auditLog = {
        id: `log_${Date.now()}`,
        action: 'user_updated',
        userId: user.id,
        userName: user.name,
        details: `Usuario ${updatedUser.name} actualizado`,
        ipAddress: '192.168.1.100',
        timestamp: new Date().toISOString(),
        severity: 'info'
      };
      dispatch({ type: ACTIONS.ADD_AUDIT_LOG, payload: auditLog });
      
      return { success: true, user: updatedUser };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, [state.users, user]);

  const deleteUser = useCallback(async (userId) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const deletedUser = state.users.find(u => u.id === userId);
      dispatch({ type: ACTIONS.DELETE_USER, payload: userId });
      
      // Add audit log
      const auditLog = {
        id: `log_${Date.now()}`,
        action: 'user_deleted',
        userId: user.id,
        userName: user.name,
        details: `Usuario ${deletedUser.name} eliminado`,
        ipAddress: '192.168.1.100',
        timestamp: new Date().toISOString(),
        severity: 'high'
      };
      dispatch({ type: ACTIONS.ADD_AUDIT_LOG, payload: auditLog });
      
      return { success: true };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, [state.users, user]);

  // Company management
  const updateCompany = useCallback(async (companyId, updates) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const updatedCompany = {
        ...state.companies.find(c => c.id === companyId),
        ...updates,
        updatedAt: new Date().toISOString()
      };
      
      dispatch({ type: ACTIONS.UPDATE_COMPANY, payload: updatedCompany });
      
      // Add audit log
      const auditLog = {
        id: `log_${Date.now()}`,
        action: 'company_updated',
        userId: user.id,
        userName: user.name,
        details: `Empresa ${updatedCompany.name} actualizada`,
        ipAddress: '192.168.1.100',
        timestamp: new Date().toISOString(),
        severity: 'info'
      };
      dispatch({ type: ACTIONS.ADD_AUDIT_LOG, payload: auditLog });
      
      return { success: true, company: updatedCompany };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, [state.companies, user]);

  // System settings
  const updateSystemSettings = useCallback(async (settings) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));
      
      dispatch({ type: ACTIONS.UPDATE_SYSTEM_SETTINGS, payload: settings });
      
      // Add audit log
      const auditLog = {
        id: `log_${Date.now()}`,
        action: 'system_settings_updated',
        userId: user.id,
        userName: user.name,
        details: `Configuración del sistema actualizada`,
        ipAddress: '192.168.1.100',
        timestamp: new Date().toISOString(),
        severity: 'high'
      };
      dispatch({ type: ACTIONS.ADD_AUDIT_LOG, payload: auditLog });
      
      return { success: true };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, [user]);

  // Maintenance mode
  const enableMaintenanceMode = useCallback(async (message, scheduledStart, scheduledEnd) => {
    const maintenanceSettings = {
      maintenance: {
        enabled: true,
        message,
        scheduledStart,
        scheduledEnd
      }
    };
    
    return await updateSystemSettings(maintenanceSettings);
  }, [updateSystemSettings]);

  const disableMaintenanceMode = useCallback(async () => {
    const maintenanceSettings = {
      maintenance: {
        enabled: false,
        message: '',
        scheduledStart: null,
        scheduledEnd: null
      }
    };
    
    return await updateSystemSettings(maintenanceSettings);
  }, [updateSystemSettings]);

  // Analytics helpers
  const getUserGrowthData = useCallback(() => {
    // Generate mock growth data
    return Array.from({ length: 30 }, (_, i) => ({
      date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      users: Math.floor(Math.random() * 10) + 15,
      companies: Math.floor(Math.random() * 3) + 1
    }));
  }, []);

  const getRevenueData = useCallback(() => {
    // Generate mock revenue data
    return Array.from({ length: 12 }, (_, i) => ({
      month: new Date(2025, i, 1).toLocaleString('es', { month: 'short' }),
      revenue: Math.floor(Math.random() * 50000) + 25000,
      shipments: Math.floor(Math.random() * 500) + 200
    }));
  }, []);

  // System health check
  const performHealthCheck = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    
    try {
      // Simulate health check
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const healthStatus = {
        database: 'healthy',
        apis: 'healthy',
        storage: 'healthy',
        cache: 'healthy',
        overall: 'good'
      };
      
      const updatedStats = {
        ...state.systemStats,
        systemHealth: healthStatus.overall,
        lastHealthCheck: new Date().toISOString()
      };
      
      dispatch({ type: ACTIONS.SET_SYSTEM_STATS, payload: updatedStats });
      
      return { success: true, health: healthStatus };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  }, [state.systemStats]);

  const value = {
    // State
    ...state,
    
    // User management
    updateUser,
    deleteUser,
    
    // Company management
    updateCompany,
    
    // System management
    updateSystemSettings,
    enableMaintenanceMode,
    disableMaintenanceMode,
    performHealthCheck,
    
    // Analytics
    getUserGrowthData,
    getRevenueData,
    
    // Utility functions
    clearError: () => dispatch({ type: ACTIONS.SET_ERROR, payload: null }),
    refreshData: loadAdminData,
    
    // Check if user is admin
    isAdmin: user?.role === 'admin'
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

// Custom hook to use the context
export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export default AdminContext;